import {
  buildLearningNodes,
  getLearningCopy,
  type LearningVisualKind,
} from './lidLearning';

export type HumanRelation =
  | 'definition'
  | 'cause'
  | 'exception'
  | 'condition'
  | 'sequence'
  | 'permission'
  | 'selection';

export interface HumanQuestionChunk {
  german: string;
  translation: string;
}

export interface HumanLearningModel {
  relation: HumanRelation;
  visual: LearningVisualKind;
  concept: string;
  signal: string;
  signalMeaning: string;
  task: string;
  chunks: HumanQuestionChunk[];
  // New fields for enhanced learning
  sharedTerms: Array<{ german: string; meaning: string }>;
  trapTerms: Array<{ word: string; meaning: string }>;
  patternHint: string;
  wordHelp: Array<{ german: string; meaning: string }>;
}

const CONCEPT_RULES: Array<[RegExp, LearningVisualKind]> = [
  [/\b(?:Rechtsstaat|Grundgesetz|Grundrecht|Menschenwürde|Gleichberechtigung|Meinungsfreiheit|Religionsfreiheit|Pressefreiheit|Versammlungsfreiheit)\w*\b/i, 'rights'],
  [/\b(?:Demokratie|Wahl|Partei|Koalition|Opposition|Petition|Bundestag|Bundesrat|Bundesregierung|Bundeskanzler|Bundespräsident|Bundesverfassungsgericht|Parlament|Gericht|Polizei)\w*\b/i, 'institution'],
  [/\b(?:Bundesland|Landtag|Gemeinde|Ministerpräsident|Föderalismus)\w*\b/i, 'state'],
  [/\b(?:Nationalsozialismus|NS-Zeit|DDR|Mauer|Wiedervereinigung|Holocaust|Europäische Union|EU)\w*\b/i, 'history'],
  [/\b(?:Gewerkschaft|Betriebsrat|Schule|Religion|Familie|Integration|Verein|Asyl|Sozialversicherung|Arbeitslosigkeit)\w*\b/i, 'society'],
];

const RELATION_RULES: Array<[HumanRelation, RegExp]> = [
  ['exception', /\b(?:nicht|kein\w*|außer|nur)\b/i],
  ['cause', /\b(?:warum|weil|denn|Grund|Folge)\b/i],
  ['condition', /\b(?:wenn|falls|Voraussetzung)\b/i],
  ['sequence', /\b(?:zuerst|danach|Reihenfolge)\b/i],
  ['permission', /\b(?:darf|dürfen|muss|müssen|kann|können)\b/i],
  ['definition', /\b(?:was|bedeutet|gemeint|bezeichnet)\b/i],
];

function meaningfulConcept(question: string) {
  for (const [pattern, visual] of CONCEPT_RULES) {
    const match = question.match(pattern);
    if (match) return { concept: match[0], visual };
  }

  const words = question.match(/\b[A-ZÄÖÜ][A-Za-zÄÖÜäöüß-]{4,}\b/g) || [];
  const ignored = new Set(['Deutschland', 'Deutschen', 'Welche', 'Welcher', 'Welches', 'Warum']);
  return {
    concept: words.find(word => !ignored.has(word)) || words[0] || question.replace(/[?.!]+$/, '').slice(0, 42),
    visual: 'everyday' as LearningVisualKind,
  };
}

function detectRelation(question: string): HumanRelation {
  for (const [relation, pattern] of RELATION_RULES) {
    if (pattern.test(question)) return relation;
  }
  return 'selection';
}


function detectRelationSignal(
  question: string,
  relation: HumanRelation,
): string | null {
  const patterns: Partial<
    Record<HumanRelation, RegExp>
  > = {
    exception:
      /\b(?:nicht|kein\w*|außer|nur)\b/i,
    cause:
      /\b(?:warum|weil|denn|Grund|Folge)\b/i,
    condition:
      /\b(?:wenn|falls|Voraussetzung)\b/i,
    sequence:
      /\b(?:zuerst|danach|Reihenfolge)\b/i,
    permission:
      /\b(?:darf|dürfen|muss|müssen|kann|können)\b/i,
    definition:
      /\b(?:was|bedeutet|gemeint|bezeichnet)\b/i,
  };

  const pattern = patterns[relation];

  if (!pattern) {
    return null;
  }

  return question.match(pattern)?.[0] || null;
}

function splitMeaningful(text: string): string[] {
  const parts = text
    .split(/(?<=[.!?;:])\s+|\s*,\s+(?=[A-ZÄÖÜ])/u)
    .map(value => value.trim())
    .filter(Boolean);
  return parts.length > 1 && parts.length <= 3 ? parts : [text.trim()];
}

function alignChunks(german: string, translation?: string | null): HumanQuestionChunk[] {
  if (!translation) return [{ german, translation: '' }];
  const germanParts = splitMeaningful(german);
  const translatedParts = splitMeaningful(translation);
  if (germanParts.length === translatedParts.length && germanParts.length > 1) {
    return germanParts.map((part, index) => ({ german: part, translation: translatedParts[index] }));
  }
  return [{ german, translation }];
}

function taskSentence(
  relation: HumanRelation,
  language: string,
  concept: string,
  signalMeaning: string,
): string {
  void relation;

  const copy = getLearningCopy(language);

  const focus =
    signalMeaning && signalMeaning !== concept
      ? signalMeaning
      : concept;

  return `${copy.questionPattern}: ${focus}. ${copy.followFlow}`;
}

// Helper functions for enhanced learning model
function extractSharedTerms(
  question: string,
  options: string[],
  nodes: ReturnType<typeof buildLearningNodes>,
): Array<{ german: string; meaning: string }> {
  const questionWords =
    question.match(
      /\b[A-ZÄÖÜ][A-Za-zÄÖÜäöüß-]{3,}\b/g
    ) || [];

  const ignoreWords = new Set([
    'Deutschland',
    'Deutschen',
    'Welche',
    'Welcher',
    'Welches',
    'Warum',
    'Was',
    'Wer',
    'Wann',
    'Wo',
    'Wie',
    'Ist',
    'Sind',
    'War',
    'Waren',
    'Hat',
    'Haben',
    'Kann',
    'Können',
    'Muss',
    'Müssen',
    'Darf',
    'Dürfen',
  ]);

  const normalizedOptions =
    options.map(option =>
      option.toLocaleLowerCase('de-DE')
    );

  return questionWords
    .filter(
      word =>
        word.length > 3
        && !ignoreWords.has(word)
    )
    .filter(word => {
      const normalized =
        word.toLocaleLowerCase('de-DE');

      return normalizedOptions.some(
        option => option.includes(normalized)
      );
    })
    .map(word => {
      const normalized =
        word.toLocaleLowerCase('de-DE');

      const node =
        nodes.find(item => {
          const german =
            item.german
              .toLocaleLowerCase('de-DE');

          return german.includes(normalized)
            || normalized.includes(german);
        });

      return {
        german: word,
        meaning: node?.meaning || '',
      };
    });
}

function extractTrapTerms(
  question: string,
  nodes: ReturnType<typeof buildLearningNodes>,
): Array<{ word: string; meaning: string }> {
  const patterns = [
    /\bmuss\s+nicht\b/i,
    /\bnicht\b/i,
    /\bnur\b/i,
    /\bkein\w*\b/i,
    /\balle\b/i,
  ];

  const meaning =
    nodes.find(
      node => node.kind === 'constraint'
    )?.meaning || '';

  const seen = new Set<string>();

  return patterns.flatMap(expression => {
    const match = question.match(expression);

    if (!match) {
      return [];
    }

    const key =
      match[0].toLocaleLowerCase('de-DE');

    if (seen.has(key)) {
      return [];
    }

    seen.add(key);

    return [{
      word: match[0],
      meaning,
    }];
  });
}

function generatePatternHint(
  language: string,
  signalMeaning: string,
): string {
  const copy = getLearningCopy(language);

  return `${copy.questionPattern}: ${signalMeaning}. ${copy.followFlow}`;
}

function extractWordHelp(
  nodes: ReturnType<typeof buildLearningNodes>,
): Array<{ german: string; meaning: string }> {
  const seen = new Set<string>();

  return nodes
    .filter(
      node =>
        Boolean(node.german.trim())
        && Boolean(node.meaning.trim())
    )
    .filter(node => {
      const key =
        node.german
          .trim()
          .toLocaleLowerCase('de-DE');

      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    })
    .map(node => ({
      german: node.german,
      meaning: node.meaning,
    }));
}

export function buildHumanLearningModel(
  question: string,
  translatedQuestion: string | null | undefined,
  language: string,
  options: string[] = [],
): HumanLearningModel {
  const relation = detectRelation(question);
  const { concept, visual } = meaningfulConcept(question);
  const nodes = buildLearningNodes(question, language);
  const signalNode =
    nodes.find(
      node =>
        node.kind === 'constraint'
        || node.kind === 'task'
    )
    || nodes[0];

  const relationSignal =
    detectRelationSignal(
      question,
      relation,
    );

  const signalMeaning =
    signalNode?.meaning
    || concept;
  
  const sharedTerms =
    extractSharedTerms(
      question,
      options,
      nodes,
    );

  const trapTerms =
    extractTrapTerms(
      question,
      nodes,
    );

  const patternHint =
    generatePatternHint(
      language,
      signalMeaning,
    );

  const wordHelp =
    extractWordHelp(nodes);
  
  return {
    relation,
    visual,
    concept,
    signal:
      relationSignal
      || signalNode?.german
      || concept,
    signalMeaning,
    task: taskSentence(relation, language, concept, signalMeaning),
    chunks: alignChunks(question, translatedQuestion),
    // Enhanced learning model fields
    sharedTerms,
    trapTerms,
    patternHint,
    wordHelp,
  };
}
